import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import { ApprovalTasksTile } from "./ApprovalTasksPage";
import { useMemo, useState, type ChangeEvent } from "react";

import { isApiClientError, isApiResponseValidationError } from "../api/errors";
import type {
  DocumentWorkflowDeadlineReport,
  DocumentWorkflowDocument,
  DocumentWorkflowExecutorReport,
} from "../entities/document-workflow";
import { shellBootstrapQuery } from "./bootstrap";
import { DocumentCard } from "./DocumentCard";

import {
  buildDocumentsQuery,
  compactSearch,
  defaultSearch,
  dueLabels,
  dueStates,
  priorities,
  priorityLabels,
  parseRegistrySearch,
  sorts,
  sortLabels,
  statuses,
  statusLabels,
  type DueState,
  type DocumentWorkflowSearch,
  type Priority,
  type Status,
  types,
  typeLabels,
  validateDocumentWorkflowSearch,
} from "./documentWorkflowSearch";
import type { ShellServices } from "./services";
import { Select } from "./Select";
import { Notice } from "./Notice";

function formatDate(value: string | null): string {
  if (value === null) return "-";
  return new Intl.DateTimeFormat("ru-RU", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(value));
}

function displayId(value: string | null): string {
  return value === null ? "-" : value.slice(0, 8);
}

/**
 * Человек в таблице: имя и ссылка на его карточку.
 *
 * Ядро отдаёт рядом с идентификатором краткую карточку, поэтому имя есть сразу.
 * Идентификатор остаётся запасным вариантом для старых записей.
 */
function PersonLink({
  id,
  person,
}: {
  id: string | null;
  person?: DocumentWorkflowDocument["assignee"];
}) {
  if (id === null) return <>-</>;
  const name = person?.display_name ?? displayId(id);
  return <><a href={`/administration/users/list?user=${id}`}>{name}</a>{person?.redirected_from ? <small className="dw-secondary">вместо {person.redirected_from.display_name}</small> : null}</>;
}

function senderEmail(document: DocumentWorkflowDocument): string {
  const email = document.sender_snapshot?.email;
  return typeof email === "string" && email !== "" ? email : "-";
}

function errorTitle(error: unknown): string {
  if (isApiResponseValidationError(error)) {
    return "Ответ API не совпал с актуальной схемой SDK. Проверьте контракт backend и generated SDK для этой операции.";
  }
  if (!isApiClientError(error)) {
    return error instanceof Error ? error.message : "Неизвестная ошибка";
  }
  const labels: Partial<Record<number, string>> = {
    401: "Сессия истекла. Войдите снова.",
    403: "Недостаточно прав для этих документов.",
    429: "Слишком много запросов. Подождите и повторите.",
    503: "Сервис документооборота временно недоступен.",
  };
  return labels[error.status] ?? `${error.message} (${error.code})`;
}

function StatusState({
  isLoading,
  error,
  empty,
  emptyText,
}: {
  isLoading: boolean;
  error: unknown;
  empty: boolean;
  emptyText: string;
}) {
  if (isLoading) {
    return <p className="dw-state">Загружаем данные документооборота...</p>;
  }
  if (error !== null) {
    return <Notice>{errorTitle(error)}</Notice>;
  }
  if (empty) {
    return <p className="dw-state">{emptyText}</p>;
  }
  return null;
}

/**
 * Состояние срока.
 *
 * Без срока документ не «в срок»: срока просто нет, и говорить о его
 * соблюдении нечего. Раньше такие документы подписывались «В срок», и по
 * таблице нельзя было отличить уложившийся документ от бессрочного.
 */
function DueBadge({ state, due }: { state: DueState; due?: string | null }) {
  if (due === null && state !== "completed") {
    return <span className="dw-due dw-due--none">Срок не задан</span>;
  }
  return (
    <span className={`dw-due dw-due--${state}`} aria-label={dueLabels[state]}>
      <span aria-hidden="true" />
      {dueLabels[state]}
    </span>
  );
}

function CountBadge({ value, label }: { value: number; label: string }) {
  if (value <= 0) return <span className="dw-muted">0</span>;
  return (
    <span className="dw-count-badge" aria-label={`${label}: ${value}`}>
      {value}
    </span>
  );
}

function DashboardTile({
  label,
  value,
  tone = "neutral",
  active,
  onClick,
}: {
  label: string;
  value: number;
  tone?: "neutral" | "warning" | "danger" | "success";
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      className={`dw-dashboard-tile dw-dashboard-tile--${tone}${active ? " dw-dashboard-tile--active" : ""}`}
      type="button"
      aria-pressed={active}
      onClick={onClick}
    >
      <span>{label}</span>
      <strong>{value}</strong>
    </button>
  );
}

function MultiSelectFilter<T extends string>({
  label,
  values,
  selected,
  labels,
  onToggle,
}: {
  label: string;
  values: readonly T[];
  selected: T[];
  labels: Record<T, string>;
  onToggle: (value: T) => void;
}) {
  return (
    <fieldset className="dw-filter-group">
      <legend>{label}</legend>
      <div className="dw-filter-pills">
        {values.map((value) => (
          <label key={value} className="dw-check-pill">
            <input
              type="checkbox"
              checked={selected.includes(value)}
              onChange={() => onToggle(value)}
            />
            <span>{labels[value]}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function ExecutorReportTable({
  report,
}: {
  report: DocumentWorkflowExecutorReport | undefined;
}) {
  const rows = report?.rows ?? [];
  return (
    <div
      className="dw-report-table"
      role="table"
      aria-label="Отчет по исполнителям"
    >
      <div role="row" className="dw-report-row dw-report-row--head">
        <span role="columnheader">Исполнитель</span>
        <span role="columnheader">Статус</span>
        <span role="columnheader">Документов</span>
      </div>
      {rows.map((row) => (
        <div
          role="row"
          className="dw-report-row"
          key={`${row.assignee_id ?? "empty"}-${row.status}`}
        >
          <span role="cell">
            <PersonLink id={row.assignee_id} person={row.assignee} />
          </span>
          <span role="cell">{statusLabels[row.status]}</span>
          <strong role="cell">{row.count}</strong>
        </div>
      ))}
    </div>
  );
}

function DeadlineReportTable({
  report,
}: {
  report: DocumentWorkflowDeadlineReport | undefined;
}) {
  const rows = report?.rows ?? [];
  return (
    <div className="dw-report-table" role="table" aria-label="Отчет по срокам">
      <div role="row" className="dw-report-row dw-report-row--head">
        <span role="columnheader">Документ</span>
        <span role="columnheader">Срок</span>
        <span role="columnheader">Состояние</span>
      </div>
      {rows.map((row) => (
        <div role="row" className="dw-report-row" key={row.document_id}>
          <span role="cell">{row.subject}</span>
          <time role="cell">
            {row.due_at === null ? "срок не задан" : formatDate(row.due_at)}
          </time>
          <span role="cell">
            <DueBadge state={row.due_state} due={row.due_at} />
          </span>
        </div>
      ))}
    </div>
  );
}

export function MailAdminPage({ services }: { services: ShellServices }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const locationSearch = useLocation({
    select: (location) => location.search as Record<string, unknown>,
  });
  const search = validateDocumentWorkflowSearch(locationSearch);
  const registrySearch = useMemo(
    () => parseRegistrySearch(search.search),
    [search.search],
  );
  const bootstrap = useQuery(shellBootstrapQuery(services));
  const currentScopeId = bootstrap.data?.currentScope?.id ?? null;
  const allOrganizations = bootstrap.data?.allOrganizations ?? false;
  const organizationName = (id: string | null): string =>
    bootstrap.data?.scopes.find((scope) => scope.id === id)?.name ?? "—";
  const currentUserId = bootstrap.data?.user.id ?? "";
  const documentsQuery = useMemo(() => buildDocumentsQuery(search), [search]);

  function updateSearch(patch: Partial<DocumentWorkflowSearch>) {
    const next = validateDocumentWorkflowSearch({
      ...search,
      ...patch,
      page: patch.page ?? 1,
    } as Record<string, unknown>);
    void navigate({
      to: "/modules/document-workflow/documents",
      search: compactSearch(next),
      // Фильтры стоят посреди страницы: после каждого нажатия экран
      // отматывался к началу, и человек терял место, где работал.
      resetScroll: false,
    });
  }

  function updateText(
    key: keyof DocumentWorkflowSearch,
    event: ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) {
    updateSearch({
      [key]: event.target.value,
    } as Partial<DocumentWorkflowSearch>);
  }

  function toggleArray<T extends Status | Priority | DueState>(
    key: "status" | "priority" | "due_state",
    value: T,
  ) {
    const selected = search[key] as T[];
    updateSearch({
      [key]: selected.includes(value)
        ? selected.filter((item) => item !== value)
        : [...selected, value],
    } as Partial<DocumentWorkflowSearch>);
  }

  function openCard(documentId: string) {
    void navigate({
      to: "/modules/document-workflow/documents",
      search: compactSearch({ ...search, document_id: documentId }),
    });
  }

  function isDashboardFilterActive(
    status: Status[],
    dueState: DueState[],
  ): boolean {
    return (
      search.status.length === status.length &&
      search.status.every((value) => status.includes(value)) &&
      search.due_state.length === dueState.length &&
      search.due_state.every((value) => dueState.includes(value))
    );
  }

  function toggleDashboardFilter(status: Status[], dueState: DueState[]) {
    updateSearch(
      isDashboardFilterActive(status, dueState)
        ? { status: [], due_state: [] }
        : { status, due_state: dueState },
    );
  }

  const documents = useQuery({
    queryKey: [
      "document-workflow",
      "registry",
      documentsQuery,
      currentScopeId,
      allOrganizations,
    ],
    queryFn: () => services.documentWorkflow.listDocuments(documentsQuery),
    enabled: currentScopeId !== null || allOrganizations,
  });
  const dashboard = useQuery({
    queryKey: [
      "document-workflow",
      "dashboard",
      currentScopeId,
      allOrganizations,
    ],
    queryFn: () => services.documentWorkflow.dashboard(),
    enabled: currentScopeId !== null || allOrganizations,
  });
  const executorReport = useQuery({
    queryKey: [
      "document-workflow",
      "reports",
      "executors",
      currentScopeId,
      allOrganizations,
      registrySearch.filters.assignee_id ?? search.assignee_id,
      search.status,
      search.received_from,
      search.received_to,
    ],
    queryFn: () =>
      services.documentWorkflow.executorReport({
        ...((registrySearch.filters.assignee_id ?? search.assignee_id) === ""
          ? {}
          : {
              user_id: registrySearch.filters.assignee_id ?? search.assignee_id,
            }),
        ...(search.status.length === 0 ? {} : { status: search.status }),
        ...(search.received_from === "" ? {} : { from: search.received_from }),
        ...(search.received_to === "" ? {} : { to: search.received_to }),
      }),
    enabled: currentScopeId !== null || allOrganizations,
  });
  const deadlineReport = useQuery({
    queryKey: [
      "document-workflow",
      "reports",
      "deadlines",
      currentScopeId,
      allOrganizations,
      registrySearch.filters.assignee_id ?? search.assignee_id,
      search.due_state,
      search.due_from,
      search.due_to,
    ],
    queryFn: () =>
      services.documentWorkflow.deadlineReport({
        ...((registrySearch.filters.assignee_id ?? search.assignee_id) === ""
          ? {}
          : {
              assignee_id:
                registrySearch.filters.assignee_id ?? search.assignee_id,
            }),
        ...(search.due_state.length === 0
          ? {}
          : { due_state: search.due_state }),
        ...(search.due_from === "" ? {} : { from: search.due_from }),
        ...(search.due_to === "" ? {} : { to: search.due_to }),
      }),
    enabled: currentScopeId !== null || allOrganizations,
  });
  const dashboardDueSoonReport = useQuery({
    queryKey: [
      "document-workflow",
      "dashboard",
      "due-soon",
      currentScopeId,
      allOrganizations,
    ],
    queryFn: () =>
      services.documentWorkflow.deadlineReport({
        due_state: ["due_soon_24h", "due_soon_2h"],
      }),
    enabled: currentScopeId !== null || allOrganizations,
  });
  const markRead = useMutation({
    mutationFn: (documentId: string) =>
      services.documentWorkflow.markRead(documentId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["document-workflow", "registry"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["document-workflow", "dashboard"],
        }),
      ]);
    },
  });

  // Карточка документа открывается из адреса: id может прийти ссылкой сразу
  // после создания, когда документа ещё нет на текущей странице реестра.

  const openedDocumentQuery = useQuery({
    queryKey: ["document-workflow", "document", search.document_id],
    queryFn: () => services.documentWorkflow.document(search.document_id),
    enabled: search.document_id !== "",
  });
  const openedDocument =
    search.document_id === ""
      ? null
      : openedDocumentQuery.isError
        ? null
        : (openedDocumentQuery.data ?? null);

  if (bootstrap.data?.currentScope === null && !allOrganizations) {
    return (
      <section className="workspace-page dw-page" aria-labelledby="dw-title">
        <div className="page-heading">
          <p className="eyebrow">Документооборот</p>
          <h1 id="dw-title">Выберите КПО</h1>
          <p>
            Dashboard и реестр строятся от текущего пользователя и выбранного
            КПО. До выбора КПО запросы к реестру не отправляются.
          </p>
        </div>
      </section>
    );
  }

  const counts = dashboard.data?.data.counts_by_status ?? {};
  const documentItems = documents.data?.data ?? [];
  const dueSoon = dashboardDueSoonReport.data?.data.rows.length ?? 0;
  const totalPages = documents.data?.meta.last_page ?? 1;
  const advancedFilterCount =
    [
      search.category_id,
      search.received_from,
      search.received_to,
      search.due_from,
      search.due_to,
      search.signature === "all" ? "" : search.signature,
      ...search.priority,
    ].filter(Boolean).length + Object.keys(registrySearch.filters).length;

  return (
    <section className="workspace-page dw-page" aria-labelledby="dw-title">
      <div className="dw-heading">
        <div>
          <p className="eyebrow">Документооборот</p>
          <h1 id="dw-title">Реестр документов</h1>
          <p>
            {allOrganizations ? (
              <>
                Данные ограничены текущим пользователем и показаны{" "}
                <strong>по всем доступным организациям</strong>.
              </>
            ) : (
              <>
                Данные ограничены текущим пользователем и КПО:{" "}
                <strong>{bootstrap.data?.currentScope?.name}</strong>.
              </>
            )}
          </p>
        </div>
        <div className="dw-heading-actions">
          <Link
            className="button button--primary"
            to="/modules/document-workflow/documents/new"
          >
            Создать документ
          </Link>
          <button
            className="button button--secondary"
            type="button"
            onClick={() => void documents.refetch()}
          >
            Обновить
          </button>
        </div>
      </div>

      {/* Статус вне сетки: элемент на всю ширину мешает auto-fit схлопнуть пустые колонки. */}
      <StatusState
        isLoading={dashboard.isLoading || dashboardDueSoonReport.isLoading}
        error={
          dashboard.isError
            ? dashboard.error
            : dashboardDueSoonReport.isError
              ? dashboardDueSoonReport.error
              : null
        }
        empty={false}
        emptyText=""
      />

      <section className="dw-dashboard" aria-label="Dashboard документов">
        <ApprovalTasksTile services={services} />
        <DashboardTile
          label="Поступили"
          value={counts.received ?? 0}
          active={isDashboardFilterActive(["received"], [])}
          onClick={() => toggleDashboardFilter(["received"], [])}
        />
        <DashboardTile
          label="Зарегистрированы"
          value={counts.registered ?? 0}
          active={isDashboardFilterActive(["registered"], [])}
          onClick={() => toggleDashboardFilter(["registered"], [])}
        />
        <DashboardTile
          label="На исполнении"
          value={counts.in_progress ?? 0}
          tone="warning"
          active={isDashboardFilterActive(["in_progress"], [])}
          onClick={() => toggleDashboardFilter(["in_progress"], [])}
        />
        <DashboardTile
          label="Приближаются к сроку"
          value={dueSoon}
          tone="warning"
          active={isDashboardFilterActive([], ["due_soon_24h", "due_soon_2h"])}
          onClick={() =>
            toggleDashboardFilter([], ["due_soon_24h", "due_soon_2h"])
          }
        />
        <DashboardTile
          label="Просрочены"
          value={dashboard.data?.data.overdue ?? 0}
          tone="danger"
          active={isDashboardFilterActive([], ["overdue"])}
          onClick={() => toggleDashboardFilter([], ["overdue"])}
        />
        <DashboardTile
          label="Исполнены"
          value={counts.completed ?? 0}
          tone="success"
          active={isDashboardFilterActive(["completed"], [])}
          onClick={() => toggleDashboardFilter(["completed"], [])}
        />
      </section>

      <section className="dw-panel" aria-labelledby="dw-filters-title">
        <div className="panel-heading dw-filter-heading">
          <div>
            <h2 id="dw-filters-title">Фильтры</h2>
            <p>
              Фильтры отправляются в API. ID-фильтры можно задать прямо в
              поиске.
            </p>
          </div>
          <div className="dw-filter-actions">
            <button
              className="button button--text"
              type="button"
              aria-expanded={showAdvancedFilters}
              aria-controls="dw-advanced-filters"
              onClick={() => setShowAdvancedFilters((value) => !value)}
            >
              {showAdvancedFilters ? "Скрыть расширенные" : "Расширенные"}
              {advancedFilterCount > 0 ? (
                <span className="dw-action-count">{advancedFilterCount}</span>
              ) : null}
            </button>
            <button
              className="button button--text"
              type="button"
              onClick={() => updateSearch(defaultSearch)}
            >
              Сбросить
            </button>
          </div>
        </div>

        <div className="dw-quick-filters" aria-label="Быстрые фильтры">
          <button
            type="button"
            className={
              search.assignee_id === currentUserId
                ? "dw-chip dw-chip--on"
                : "dw-chip"
            }
            onClick={() =>
              updateSearch({
                assignee_id:
                  search.assignee_id === currentUserId ? "" : currentUserId,
              })
            }
          >
            На моем исполнении
          </button>
          <button
            type="button"
            className={
              search.controller_id === currentUserId
                ? "dw-chip dw-chip--on"
                : "dw-chip"
            }
            onClick={() =>
              updateSearch({
                controller_id:
                  search.controller_id === currentUserId ? "" : currentUserId,
              })
            }
          >
            Я контролер
          </button>
          <button
            type="button"
            className={
              search.unread === "true" ? "dw-chip dw-chip--on" : "dw-chip"
            }
            onClick={() =>
              updateSearch({
                unread: search.unread === "true" ? "all" : "true",
              })
            }
          >
            Непрочитанные
          </button>
        </div>

        <div className="dw-filter-grid dw-filter-grid--primary">
          <label className="field">
            <span>Поиск</span>
            <input
              value={search.search}
              onChange={(event) => updateText("search", event)}
              placeholder="Тема, номер или описание; например: исполнитель:UUID"
            />
            <small className="dw-search-help">
              Несколько условий:{" "}
              <code>договор, категория:UUID, папка:UUID</code>
            </small>
          </label>
          <label className="field">
            <span>Тип</span>
            <Select
              value={search.type}
              onChange={(event) => updateText("type", event)}
            >
              <option value="">Все</option>
              {types.map((type) => (
                <option key={type} value={type}>
                  {typeLabels[type]}
                </option>
              ))}
            </Select>
          </label>
          <label className="field">
            <span>Сортировка</span>
            <Select
              value={search.sort}
              onChange={(event) => updateText("sort", event)}
            >
              {sorts.map((sort) => (
                <option key={sort} value={sort}>
                  {sortLabels[sort]}
                </option>
              ))}
            </Select>
          </label>
        </div>

        <MultiSelectFilter<Status>
          label="Статус"
          values={statuses}
          selected={search.status}
          labels={statusLabels}
          onToggle={(value) => toggleArray("status", value)}
        />
        <MultiSelectFilter
          label="Приоритет"
          values={priorities}
          selected={search.priority}
          labels={priorityLabels}
          onToggle={(value) => toggleArray("priority", value)}
        />
        {showAdvancedFilters ? (
          <div className="dw-advanced-filters" id="dw-advanced-filters">
            <div className="dw-filter-grid">
              <label className="field">
                <span>Получено с</span>
                <input
                  type="date"
                  value={search.received_from}
                  onChange={(event) => updateText("received_from", event)}
                />
              </label>
              <label className="field">
                <span>Получено до</span>
                <input
                  type="date"
                  value={search.received_to}
                  onChange={(event) => updateText("received_to", event)}
                />
              </label>
              <label className="field">
                <span>Срок с</span>
                <input
                  type="date"
                  value={search.due_from}
                  onChange={(event) => updateText("due_from", event)}
                />
              </label>
              <label className="field">
                <span>Срок до</span>
                <input
                  type="date"
                  value={search.due_to}
                  onChange={(event) => updateText("due_to", event)}
                />
              </label>
              <label className="field">
                <span>Прочтение карточки</span>
                <Select
                  value={search.unread}
                  onChange={(event) => updateText("unread", event)}
                >
                  <option value="all">Все</option>
                  <option value="true">Непрочитанные</option>
                  <option value="false">Прочитанные</option>
                </Select>
              </label>
              <label className="field">
                <span>Подпись</span>
                <Select
                  value={search.signature}
                  onChange={(event) => updateText("signature", event)}
                >
                  <option value="all">Все</option>
                  <option value="signed">Есть подпись</option>
                  <option value="unsigned">Без подписи</option>
                </Select>
              </label>
            </div>
            <MultiSelectFilter
              label="Срок"
              values={dueStates}
              selected={search.due_state}
              labels={dueLabels}
              onToggle={(value) => toggleArray("due_state", value)}
            />
          </div>
        ) : null}
      </section>

      <section className="dw-panel" aria-labelledby="dw-table-title">
        <div className="panel-heading">
          <div>
            <h2 id="dw-table-title">Документы</h2>
            <p>{documents.data?.meta.total ?? 0} найдено</p>
          </div>
          <label className="dw-page-size">
            <span>На странице</span>
            <Select
              value={search.per_page}
              onChange={(event) =>
                updateSearch({ per_page: Number(event.target.value) })
              }
            >
              {[10, 25, 50, 100].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </Select>
          </label>
        </div>
        <StatusState
          isLoading={documents.isLoading}
          error={documents.isError ? documents.error : null}
          empty={documentItems.length === 0}
          emptyText="По текущим фильтрам документов нет."
        />
        {documentItems.length > 0 ? (
          <div className="dw-table-wrap">
            <table className="dw-table">
              <thead>
                <tr>
                  <th>Документ</th>
                  {allOrganizations ? <th>Организация</th> : null}
                  <th className="dw-cell-center">Статус</th>
                  <th className="dw-cell-center">Срок</th>
                  <th>Люди</th>
                  <th>Связи</th>
                  <th>Прочтение</th>
                </tr>
              </thead>
              <tbody>
                {documentItems.map((document) => (
                  <tr
                    key={document.id}
                    className={`dw-row--${document.due_state}`}
                  >
                    <td data-label="Документ">
                      <div className="dw-doc-title">
                        <button
                          className="dw-document-open"
                          type="button"
                          onClick={() => openCard(document.id)}
                        >
                          {document.subject}
                        </button>
                        <span>
                          {typeLabels[document.type]} ·{" "}
                          {document.registration?.number ??
                            document.incoming_number ??
                            "без номера"}
                        </span>
                        <small>
                          {document.counterparty?.name ?? senderEmail(document)}
                        </small>
                      </div>
                    </td>
                    {allOrganizations ? (
                      <td data-label="Организация">
                        {organizationName(document.access_scope_id)}
                      </td>
                    ) : null}
                    <td className="dw-cell-center" data-label="Статус">
                      <div className="dw-cell-stack">
                        <span className="dw-status">
                          {statusLabels[document.status]}
                        </span>
                        <span className="dw-priority">
                          {priorityLabels[document.priority]}
                        </span>
                      </div>
                    </td>
                    <td className="dw-cell-center" data-label="Срок">
                      <div className="dw-cell-stack">
                        <DueBadge
                          state={document.due_state}
                          due={document.due_at}
                        />
                        {document.due_at === null ? null : (
                          <time>{formatDate(document.due_at)}</time>
                        )}
                      </div>
                    </td>
                    <td data-label="Люди">
                      <dl className="dw-people">
                        <div>
                          <dt>Исп.</dt>
                          <dd>
                            <PersonLink
                              id={document.assignee_id}
                              person={document.assignee}
                            />
                          </dd>
                        </div>
                        <div>
                          <dt>Контр.</dt>
                          <dd>
                            <PersonLink
                              id={document.controller_id}
                              person={document.controller}
                            />
                          </dd>
                        </div>
                        <div>
                          <dt>Получ.</dt>
                          <dd>
                            {document.recipient_ids?.length
                              ? document.recipient_ids.length
                              : "нет"}
                          </dd>
                        </div>
                      </dl>
                    </td>
                    <td data-label="Связи">
                      <div className="dw-links">
                        <span>
                          {document.has_discussion ||
                          document.latest_internal_at
                            ? "Есть обсуждение"
                            : "Обсуждения нет"}
                          {(document.unread_comment_count ?? 0) > 0
                            ? ` · новых: ${document.unread_comment_count}`
                            : ""}
                        </span>
                        <span>
                          Внешняя переписка:{" "}
                          {document.latest_external_at === null
                            ? "нет"
                            : "есть"}
                        </span>
                      </div>
                    </td>
                    <td data-label="Прочтение">
                      {document.unread ? (
                        <button
                          type="button"
                          className="dw-read-action"
                          disabled={markRead.isPending}
                          onClick={() => markRead.mutate(document.id)}
                        >
                          <CountBadge
                            value={1}
                            label="Непрочитанная карточка"
                          />
                          <span>Непрочитано</span>
                        </button>
                      ) : (
                        <span className="dw-read-ok">Прочитано</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        <div className="dw-pagination" aria-label="Пагинация документов">
          <button
            className="button button--secondary"
            type="button"
            disabled={search.page <= 1}
            onClick={() => updateSearch({ page: search.page - 1 })}
          >
            Назад
          </button>
          <span>
            Страница {search.page} из {totalPages}
          </span>
          <button
            className="button button--secondary"
            type="button"
            disabled={search.page >= totalPages}
            onClick={() => updateSearch({ page: search.page + 1 })}
          >
            Вперед
          </button>
        </div>
      </section>

      <section className="dw-reports" aria-label="Отчеты документооборота">
        <article className="dw-panel">
          <div className="panel-heading">
            <div>
              <p className="eyebrow">Отчет</p>
              <h2>По исполнителям</h2>
            </div>
          </div>
          <StatusState
            isLoading={executorReport.isLoading}
            error={executorReport.isError ? executorReport.error : null}
            empty={(executorReport.data?.data.rows.length ?? 0) === 0}
            emptyText="Нет данных по исполнителям за выбранный период."
          />
          {(executorReport.data?.data.rows.length ?? 0) > 0 ? (
            <ExecutorReportTable report={executorReport.data?.data} />
          ) : null}
        </article>
        <article className="dw-panel">
          <div className="panel-heading">
            <div>
              <p className="eyebrow">Отчет</p>
              <h2>По срокам</h2>
            </div>
          </div>
          <StatusState
            isLoading={deadlineReport.isLoading}
            error={deadlineReport.isError ? deadlineReport.error : null}
            empty={(deadlineReport.data?.data.rows.length ?? 0) === 0}
            emptyText="Нет документов со сроками по выбранным условиям."
          />
          {(deadlineReport.data?.data.rows.length ?? 0) > 0 ? (
            <DeadlineReportTable report={deadlineReport.data?.data} />
          ) : null}
        </article>
      </section>
      {search.document_id && openedDocumentQuery.isPending ? (
        <Notice role="status">Загружаем карточку документа…</Notice>
      ) : null}
      {search.document_id && openedDocumentQuery.isError ? (
        <section role="alert">
          <p>{errorTitle(openedDocumentQuery.error)}</p>
          <button onClick={() => void openedDocumentQuery.refetch()}>
            Повторить загрузку карточки
          </button>
          <button onClick={() => openCard("")}>Закрыть карточку</button>
        </section>
      ) : null}
      {openedDocument === null ? null : (
        <DocumentCard
          key={openedDocument.id}
          initialTab={search.tab}
          targetId={search.message}
          onTabChange={(tab, target) =>
            updateSearch(
              target === undefined ? { tab } : { tab, message: target },
            )
          }
          document={openedDocument}
          services={services}
          onClose={() => openCard("")}
        />
      )}
    </section>
  );
}
